import { useParams } from "react-router-dom";
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import {
  FILE_URL,
  Roles,
  Suspect,
  SuspectTowitness,
  SuspectToWitnessDecision,
  SuspectToWitnessStatus,
} from "types";
import { viewIcon } from "utils/constant";
import { errorHandlerOnForm, hasRole } from "utils/helper";
import ImageModal from "components/ImageModal";
import { useAuth } from "context/AuthContext";
import { useTooltip } from "context/TooltipProvider";
import SuspectDetailInfo from "pages/Incident/IncidentDetail/modals/SuspectDetailInfo";
import {
  editSuspectToWitness2,
  fetchSuspectToWitnessById,
} from "services/caseServices";
import EditSuspectToWitnessForm from "./fragment/EditSuspectToWitnessForm";
import RegistrarAction from "./fragment/RegistrarAction";
import RevokeRequest from "./fragment/RevokeRequest";
import SuspectToWitnessFeedbacks from "./fragment/SuspectToWitnessFeedback";
import DocumentViewerModal from "components/DocumentViewer";
import { useTranslation } from "react-i18next";

type Props = {
  selectedId?: string | number;
};

const SuspectToWitnessDetail: React.FC<Props> = ({ selectedId: selectedProp }) => {
  // Opened inside the investigation file (prop) or at /suspect-to-witness/:id.
  const { id: routeId } = useParams();
  const selectedId = selectedProp ?? routeId;
  const { t } = useTranslation();
  const { loginData } = useAuth();
  const { showTooltip } = useTooltip();

  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [isSuspectDetailOpen, setIsSuspectDetailOpen] = useState(false);
  const [isEditFormOpen, setIsEditFormOpen] = useState(false);
  const [isRegistrarActionOpen, setIsRegistrarActionOpen] = useState(false);
  const [isRevokeRequestOpen, setIsRevokeRequestOpen] = useState(false);
  const [conversion, setConversion] = useState<SuspectTowitness>();

  const isProsecutor = hasRole(loginData?.roles, [Roles.prosecutor]);
  const isRegistrar = hasRole(loginData?.roles, [Roles.registrar]);
  const isCoordinator = hasRole(loginData?.roles, [Roles.coordinator]);
  const isDirector = hasRole(loginData?.roles, [
    Roles.economicDirector,
    Roles.prosecutionDirector,
  ]);
  const isAttorney = hasRole(loginData?.roles, [Roles.generalAttorney]);
  const isDeputy = hasRole(loginData?.roles, [Roles.deputy]);

  const fetchData = () => {
    if (!selectedId) return;

    fetchSuspectToWitnessById(selectedId)
      .then((data) => {
        setConversion(data);
      })
      .catch((error) => {
        showTooltip(
          errorHandlerOnForm(error) || t("error_fetching_case_details"),
          "error",
        );
      });
  };

  useEffect(() => {
    fetchData();
  }, [selectedId]);

  const updateStatus = (status: string) => {
    if (!selectedId) return;
    editSuspectToWitness2({
      id: selectedId,
      status,
    })
      .then(() => {
        showTooltip(t("successfully_updated"), "success");
        fetchData();
      })
      .catch((error) => {
        showTooltip(
          error?.response?.data?.detail || errorHandlerOnForm(error),
          "error",
        );
      });
  };

  const updateDecision = (decision: string) => {
    if (!selectedId) return;
    editSuspectToWitness2({
      id: selectedId,
      decision: conversion?.revocation_reason ? undefined : decision,
      revocation_decision: conversion?.revocation_reason ? decision : undefined,
      immunity_revoked:
        conversion?.revocation_reason && decision === "Approved"
          ? true
          : undefined,
      status:
        decision === "Approved"
          ? "forwarded_to_registrar"
          : decision === "Respond_Back"
            ? "Respond_Back_prosecutor"
            : undefined,
    })
      .then(() => {
        showTooltip(t("successfully_updated"), "success");
        fetchData();
      })
      .catch((error) => {
        showTooltip(
          error?.response?.data?.detail || errorHandlerOnForm(error),
          "error",
        );
      });
  };

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
            <span className="text-2xl">🔄</span>
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {t("suspect_to_witness_conversion")}
            </h1>
            <p className="text-gray-600 mt-1">
              {t("manage_suspect_to_witness_conversion_requests_and_decisions")}
            </p>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      {conversion?.decision !== "Rejected" && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex flex-wrap gap-3">
            {/* Coordinator */}
            {isCoordinator &&
              conversion?.status === "forwarded_to_cordinator" &&
              !conversion?.org_id && (
                <button
                  type="button"
                  className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700 transition-colors"
                  onClick={async () => {
                    editSuspectToWitness2({
                      id: selectedId,
                      org_id: "Forward to Director",
                    })
                      .then(() => {
                        showTooltip("Successfully Updated!", "success");
                        fetchData();
                      })
                      .catch((error) => {
                        showTooltip(
                          error?.response?.data?.detail ||
                            errorHandlerOnForm(error),
                          "error",
                        );
                      });
                  }}
                >
                  {t("forward_to_director")}
                </button>
              )}
            {/* {isCoordinator &&
              conversion?.status === "forwarded_to_cordinator" &&
              !conversion?.org_id && (
                <button
                  type="button"
                  className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700 transition-colors"
                  onClick={() => {
                    updateStatus("forwarded_to_attorney_general");
                  }}
                >
                  Forward to Attorney General
                </button>
              )} */}

            {/* Director */}
            {isDirector &&
              conversion?.status === "forwarded_to_cordinator" &&
              conversion?.org_id && (
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700 transition-colors"
                    onClick={async () => {
                      updateStatus("forwarded_to_attorney_general");
                    }}
                  >
                    {t("forward_to_deputy")}
                  </button>
                  <button
                    type="button"
                    className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700 transition-colors"
                    onClick={async () => {
                      editSuspectToWitness2({
                        id: selectedId,
                        org_id: null,
                        decision: "Rejected",
                      })
                        .then(() => {
                          showTooltip(t("successfully_rejected"), "success");
                          fetchData();
                        })
                        .catch((error) => {
                          showTooltip(
                            error?.response?.data?.detail ||
                              errorHandlerOnForm(error),
                            "error",
                          );
                        });
                    }}
                  >
                    {t("reject")}
                  </button>
                </div>
              )}

            {/* Deputy */}
            {isDeputy &&
              conversion?.status === "forwarded_to_attorney_general" &&
              conversion?.org_id && (
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700 transition-colors"
                    onClick={async () => {
                      updateStatus("forwarded_to_head_of_attorney_general");
                    }}
                  >
                    {t("forward_to_attorney")}
                  </button>
                  <button
                    type="button"
                    className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700 transition-colors"
                    onClick={async () => {
                      editSuspectToWitness2({
                        id: selectedId,
                        org_id: null,
                        decision: "Rejected",
                      })
                        .then(() => {
                          showTooltip(t("successfully_rejected"), "success");
                          fetchData();
                        })
                        .catch((error) => {
                          showTooltip(
                            error?.response?.data?.detail ||
                              errorHandlerOnForm(error),
                            "error",
                          );
                        });
                    }}
                  >
                    {t("reject")}
                  </button>
                </div>
              )}

            {/* Atorny */}
            {isAttorney &&
              conversion?.status ===
                "forwarded_to_head_of_attorney_general" && (
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    className="px-4 py-2 rounded-lg bg-green-600 text-white text-sm font-medium hover:bg-green-700 transition-colors"
                    onClick={() => {
                      updateDecision("Approved");
                    }}
                  >
                    {t("approve")}
                  </button>
                  <button
                    type="button"
                    className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition-colors"
                    onClick={() => {
                      updateDecision("Respond_Back");
                    }}
                  >
                    {t("respond_back")}
                  </button>
                  <button
                    type="button"
                    className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700 transition-colors"
                    onClick={() => {
                      updateDecision("Rejected");
                    }}
                  >
                    {t("reject")}
                  </button>
                </div>
              )}
            {isProsecutor &&
              (conversion?.status === "Pending" ||
                conversion?.status === "Respond_Back_prosecutor") &&
              !conversion?.revocation_reason && (
                <>
                  <button
                    type="button"
                    className="px-4 py-2 rounded-lg bg-gray-800 text-white text-sm font-medium hover:bg-gray-900 transition-colors"
                    onClick={() => {
                      setIsEditFormOpen(true);
                    }}
                  >
                    {t("edit_request")}
                  </button>
                  <button
                    type="button"
                    className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700 transition-colors"
                    onClick={() => {
                      updateStatus("forwarded_to_cordinator");
                    }}
                  >
                    {t("forward_to_coordinator")}
                  </button>
                </>
              )}
            {isCoordinator &&
              conversion?.status === "forwarded_to_cordinator" &&
              !conversion?.org_id && (
                <button
                  type="button"
                  className="px-4 py-2 rounded-lg bg-gray-200 text-gray-800 text-sm font-medium hover:bg-gray-300 transition-colors"
                  onClick={() => {
                    updateStatus("Respond_Back_prosecutor");
                  }}
                >
                  {t("respond_back")}
                </button>
              )}
            {isRegistrar && conversion?.status === "forwarded_to_registrar" && (
              <button
                type="button"
                className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700 transition-colors"
                onClick={() => {
                  setIsRegistrarActionOpen(true);
                }}
              >
                {t("send_to_court")}
              </button>
            )}
            {isProsecutor &&
              (conversion?.status === "sent_to_court" ||
                (conversion?.status === "Respond_Back_prosecutor" &&
                  conversion?.revocation_reason)) && (
                <button
                  type="button"
                  className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700 transition-colors"
                  onClick={() => {
                    setIsRevokeRequestOpen(true);
                  }}
                >
                  {conversion?.revocation_reason
                    ? t("edit_revoke_request")
                    : t("request_revoke")}
                </button>
              )}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
              <div>
                <h2 className="text-xl font-bold text-gray-900">
                  {t("case_details")}
                </h2>
                <p className="text-gray-600 mt-1">
                  {t("complete_information_about_the_conversion_request")}
                </p>
              </div>
              {conversion?.status && (
                <span
                  className={`inline-block px-3 py-1 rounded-full font-semibold text-sm ${getStatusColor(
                    conversion.status,
                  )}`}
                >
                  {conversion.status === "forwarded_to_cordinator" &&
                  conversion?.org_id
                    ? t("forward_to_director")
                    : conversion?.status === "forwarded_to_attorney_general"
                      ? t("forward_to_deputy")
                      : t(`status_${conversion.status.toLowerCase()}`, {
                          defaultValue:
                            SuspectToWitnessStatus[conversion.status] ||
                            conversion.status,
                        })}
                </span>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                {
                  label: t("crime_registration_number"),
                  value: conversion?.crime_registration_number,
                },
                {
                  label: t("incident_registration_number"),
                  value: conversion?.incident_registration_number,
                },
                {
                  label: t("prosecutor_registration_number"),
                  value: conversion?.prosecutor_registration_number,
                },
                {
                  label: t("agreement_with_suspect"),
                  value: conversion?.agreement_with_suspect
                    ? t("yes")
                    : t("no"),
                },
                {
                  label: t("request_date"),
                  value: conversion?.request_date
                    ? new Date(conversion?.request_date).toLocaleDateString()
                    : t("na"),
                },
                {
                  label: t("decision"),
                  value: conversion?.decision
                    ? t(
                        `suspect_to_witness_decision_${conversion.decision.toLowerCase()}`,
                        {
                          defaultValue:
                            SuspectToWitnessDecision[conversion?.decision] ||
                            conversion.decision,
                        },
                      )
                    : t("na"),
                },
                {
                  label: t("immunity_revoked"),
                  value: conversion?.immunity_revoked ? t("yes") : t("no"),
                },
                {
                  label: t("revoked_by_attorney_general"),
                  value: conversion?.revoked_by_attorney_general
                    ? t("yes")
                    : t("no"),
                },
                { label: t("prosecutor"), value: conversion?.prosecutor_name },
              ]?.map((info, index) => (
                <div key={index} className="space-y-1">
                  <p className="text-sm font-medium text-gray-600">
                    {info.label}
                  </p>
                  <p className="text-sm font-semibold text-gray-900">
                    {info.value || t("na")}
                  </p>
                </div>
              ))}
            </div>

            <DocumentViewerModal
              documents={(conversion?.documents || []) as any}
              baseUrl={`${process.env.REACT_APP_API_BASE_URL}${FILE_URL.SUSPECT_TO_WITNESS}`}
              layout="list"
              // label="Letter Upload"
              hideEmptyFile={true}
            />

            {(conversion?.conversion_summary ||
              conversion?.revocation_reason) && (
              <div className="mt-8 space-y-4">
                {conversion?.conversion_summary && (
                  <div>
                    <p className="text-sm font-medium text-gray-600 mb-2">
                      {t("conversion_summary")}
                    </p>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-sm text-gray-700">
                        {conversion.conversion_summary}
                      </p>
                    </div>
                  </div>
                )}
                {conversion?.revocation_reason && (
                  <div>
                    <p className="text-sm font-medium text-gray-600 mb-2">
                      {t("revocation_reason")}
                    </p>
                    <div className="bg-red-50 p-4 rounded-lg">
                      <p className="text-sm text-gray-700">
                        {conversion.revocation_reason}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Suspect Information */}
            {conversion?.suspect_info && (
              <div className="mt-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  {t("suspect_information")}
                </h3>
                <div className="bg-gray-50 rounded-lg p-6">
                  <div className="flex flex-col md:flex-row md:items-center gap-6">
                    <div className="flex-shrink-0">
                      <img
                        src={
                          conversion?.suspect_info?.picture_file ||
                          require("../../images/imageVector.png")
                        }
                        alt={t("suspect")}
                        className="w-24 h-24 rounded-lg object-cover cursor-pointer hover:opacity-90 transition-opacity"
                        onClick={() => setIsImageModalOpen(true)}
                      />
                    </div>
                    <div className="flex-1">
                      <h4 className="text-xl font-bold text-gray-900 mb-2">
                        {`${conversion?.suspect_info?.first_name} ${conversion?.suspect_info?.middle_name} ${conversion?.suspect_info?.last_name}`}
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm text-gray-600">{t("age")}</p>
                          <p className="text-sm font-semibold text-gray-900">
                            {conversion?.suspect_info?.age || t("na")}{" "}
                            {t("years_old_label")}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">
                            {t("education_level")}
                          </p>
                          <p className="text-sm font-semibold text-gray-900">
                            {conversion?.suspect_info?.education_level ||
                              t("na")}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">
                            {t("nationality")}
                          </p>
                          <p className="text-sm font-semibold text-gray-900">
                            {conversion?.suspect_info?.nationality || t("na")}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">
                            {t("phone_number")}
                          </p>
                          <p className="text-sm font-semibold text-gray-900">
                            {conversion?.suspect_info?.phone_number || t("na")}
                          </p>
                        </div>
                      </div>
                      <div className="mt-4">
                        <button
                          type="button"
                          onClick={() => setIsSuspectDetailOpen(true)}
                          className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                        >
                          {viewIcon}
                          {t("view_full_details")}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Attachments */}
            {(conversion?.agreement_with_suspect_file_info ||
              conversion?.revocation_file_info) && (
              <div className="mt-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  {t("attachments")}
                </h3>
                <div className="space-y-4">
                  {conversion?.agreement_with_suspect_file_info && (
                    <div className="flex items-center justify-between bg-gray-50 p-4 rounded-lg">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                          <span className="text-xl">📄</span>
                        </div>
                        <div>
                          <p className="font-semibold text-gray-900">
                            {t("suspect_to_witness_agreement")}
                          </p>
                          <p className="text-sm text-gray-600">
                            {conversion?.agreement_with_suspect_file_info
                              .file_name || t("document")}
                          </p>
                        </div>
                      </div>
                      <a
                        href={
                          conversion?.agreement_with_suspect_file_info
                            .file_url || ""
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                      >
                        {t("view")}
                      </a>
                    </div>
                  )}
                  {conversion?.revocation_file_info && (
                    <div className="flex items-center justify-between bg-gray-50 p-4 rounded-lg">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                          <span className="text-xl">📄</span>
                        </div>
                        <div>
                          <p className="font-semibold text-gray-900">
                            {t("revocation_file")}
                          </p>
                          <p className="text-sm text-gray-600">
                            {conversion?.revocation_file_info.file_name ||
                              t("document")}
                          </p>
                        </div>
                      </div>
                      <a
                        href={conversion?.revocation_file_info.file_url || ""}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                      >
                        {t("view")}
                      </a>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {conversion && (
            <SuspectToWitnessFeedbacks
              conversion={conversion}
              respond_back_comments={conversion?.respond_back_comments || []}
              setRefetch={fetchData}
            />
          )}
        </div>
      </div>

      {/* Modals */}
      {isImageModalOpen && (
        <ImageModal
          isOpen={isImageModalOpen}
          imageSrc={
            conversion?.suspect_info?.picture_file ||
            require("../../images/imageVector.png")
          }
          label={`${conversion?.suspect_info?.first_name} ${conversion?.suspect_info?.last_name}`}
          onClose={() => setIsImageModalOpen(false)}
        />
      )}
      {isSuspectDetailOpen && (
        <SuspectDetailInfo
          suspect={conversion?.suspect_info as Suspect}
          isOpen={isSuspectDetailOpen}
          onClose={() => setIsSuspectDetailOpen(false)}
        />
      )}
      {isEditFormOpen && conversion && (
        <EditSuspectToWitnessForm
          conversion={conversion}
          onClose={(reload) => {
            setIsEditFormOpen(false);
            if (reload) fetchData();
          }}
        />
      )}
      {isRegistrarActionOpen && conversion && (
        <RegistrarAction
          conversion={conversion}
          onClose={(reload) => {
            setIsRegistrarActionOpen(false);
            if (reload) fetchData();
          }}
        />
      )}
      {isRevokeRequestOpen && conversion && (
        <RevokeRequest
          conversion={conversion}
          onClose={(reload) => {
            setIsRevokeRequestOpen(false);
            if (reload) fetchData();
          }}
        />
      )}
    </div>
  );
};

// Helper function for status colors
const getStatusColor = (status: string) => {
  switch (status.toLowerCase()) {
    case "pending":
      return "bg-yellow-100 text-yellow-800";
    case "forwarded_to_cordinator":
      return "bg-blue-100 text-blue-800";
    case "forwarded_to_attorney_general":
      return "bg-purple-100 text-purple-800";
    case "forwarded_to_registrar":
      return "bg-green-100 text-green-800";
    case "sent_to_court":
      return "bg-red-100 text-red-800";
    case "respond_back_prosecutor":
      return "bg-orange-100 text-orange-800";
    default:
      return "bg-gray-100 text-gray-800";
  }
};

export default SuspectToWitnessDetail;
